import { useSyncExternalStore } from "react";

/**
 * Order Store - localStorage-based order persistence
 * Enhanced: expanded statuses, auto WhatsApp messages, invoice editing, tracking
 * Orders are also synced to cloud via edge function for persistence
 */

import { decrementStock } from "@/stores/stockStore";
import { getPriceKey, type Grade, type ScreenCondition } from "./catalog";
import { loadAdminSettings } from "@/stores/adminStore";
import { supabase } from "@/integrations/supabase/client";
import type { OrderTracking } from "./trackingStore";

export type OrderStatus = 
  | "Pending" | "Confirmed" | "Shipped" | "At Warehouse" 
  | "Outbound" | "Inbound" | "Arrived" | "Delivering" 
  | "Delivered" | "Completed" | "Enquiry" | "Refunded";

export interface SmsLogEntry {
  id: string;
  type: "confirmed" | "shipped" | "delivered" | "manual" | "status_update";
  sentAt: number;
  toNumber: string;
  message: string;
  status: "sent" | "failed";
}

export type OrderSource = "storefront" | "deal" | "enquiry" | "referral" | "swap";

export interface Order {
  id: string;
  invoiceId: string;
  deviceName: string;
  variant: string;
  grade: string;
  screenType?: string;
  color?: string;
  finalPrice: number;
  /** Pre-discount price (only set when a discount was applied at order time). */
  originalPrice?: number;
  /** Discount percent applied (e.g. 10 for 10% OFF). */
  discountPercent?: number;
  customerName: string;
  location: string;
  status: OrderStatus;
  timestamp: number;
  shippingFee: number;
  shippingLabel?: string;
  /** ID of the selected ShippingOption (used for auto-applying linked tracking preset). */
  shippingOptionId?: string;
  staffAssigned?: string;
  smsOptIn?: boolean;
  customerPhone?: string;
  /** Customer email captured at order time (for transactional notifications). */
  customerEmail?: string;
  smsLog?: SmsLogEntry[];
  referredBy?: string;
  orderSource?: OrderSource;
  tracking?: OrderTracking;
  /** Linked customer auth user id (when customer was signed in at order time). */
  customerUserId?: string;
}

const ORDERS_KEY = "khays_orders";
const ORDERS_EVENT = "khays-orders-updated";
let ordersRevision = 0;

function emitOrdersChanged(): void {
  ordersRevision += 1;
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(ORDERS_EVENT));
  }
}

function subscribeOrders(listener: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(ORDERS_EVENT, listener);
  return () => window.removeEventListener(ORDERS_EVENT, listener);
}

export function useOrdersRevision(): number {
  return useSyncExternalStore(subscribeOrders, () => ordersRevision, () => ordersRevision);
}

function generateInvoiceId(): string {
  const now = new Date();
  const dateStr = now.toISOString().slice(0, 10).replace(/-/g, "");
  const rand = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `KPO-${dateStr}-${rand}`;
}

export function generateOrderId(): string {
  return crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

export function loadOrders(): Order[] {
  try {
    const stored = localStorage.getItem(ORDERS_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return [];
}

export function saveOrders(orders: Order[]): void {
  localStorage.setItem(ORDERS_KEY, JSON.stringify(orders));
  emitOrdersChanged();
}

export function addOrder(orderData: Omit<Order, "id" | "invoiceId" | "status" | "timestamp" | "shippingFee" | "smsLog"> & { status?: OrderStatus; shippingFee?: number; shippingLabel?: string }): Order {
  const orders = loadOrders();
  const settings = loadAdminSettings();
  const order: Order = {
    ...orderData,
    id: generateOrderId(),
    invoiceId: generateInvoiceId(),
    status: orderData.status || "Pending",
    timestamp: Date.now(),
    shippingFee: orderData.status === "Enquiry" ? 0 : (orderData.shippingFee ?? settings.shippingFee ?? 500),
    shippingLabel: orderData.shippingLabel,
    smsLog: [],
    referredBy: orderData.referredBy,
  };

  // Auto-assign staff: use referral if present, otherwise assign to admin
  if (order.referredBy) {
    order.staffAssigned = order.referredBy;
  } else if (!order.staffAssigned) {
    order.staffAssigned = "admin";
  }

  orders.unshift(order);
  saveOrders(orders);

  // Sync order to cloud via edge function (fire-and-forget)
  syncOrderToCloud(order);

  return order;
}

/** Sync a single order to the cloud edge function */
async function syncOrderToCloud(order: Order): Promise<void> {
  try {
    const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
    const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
    if (!projectId || !anonKey) return;
    
    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/submit-order`;
    const { getCurrentTenantId } = await import("@/lib/tenantContext");
    const tenantId = getCurrentTenantId();
    // Phase 4 — record conversion event for analytics (tenant-scoped via tenantId).
    try {
      const { logCustomerActivity } = await import("@/data/customerActivity");
      void logCustomerActivity("checkout_success", {
        orderId: order.id,
        deviceName: order.deviceName,
        finalPrice: order.finalPrice,
      });
    } catch { /* noop */ }
    await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${anonKey}`,
      },
      body: JSON.stringify({ order, tenantId }),
    });
  } catch (e) {
    console.warn("Failed to sync order to cloud:", e);
  }
}

export function updateOrderStatus(orderId: string, status: OrderStatus): void {
  const orders = loadOrders();
  const idx = orders.findIndex((o) => o.id === orderId);
  if (idx < 0) return;
  const prevStatus = orders[idx].status;
  if (prevStatus === status) return;
  orders[idx].status = status;

  // Auto-apply linked tracking preset on Confirmed (no preset assigned yet).
  if (status === "Confirmed" && !orders[idx].tracking) {
    void (async () => {
      try {
        const [{ useShippingStore }, { loadTrackingPresets, createTrackingFromPreset }] = await Promise.all([
          import("@/stores/shippingStore"),
          import("@/data/trackingStore"),
        ]);
        const shipId = orders[idx].shippingOptionId;
        if (!shipId) return;
        const opt = useShippingStore.getState().options.find(o => o.id === shipId);
        const presetId = opt?.linkedTrackingPresetId;
        if (!presetId) return;
        const preset = loadTrackingPresets().find(p => p.id === presetId);
        if (!preset) return;
        // Re-read & write to avoid clobbering concurrent edits.
        const fresh = loadOrders();
        const fi = fresh.findIndex(o => o.id === orderId);
        if (fi < 0 || fresh[fi].tracking) return;
        fresh[fi].tracking = createTrackingFromPreset(preset, status);
        saveOrders(fresh);
      } catch (e) { console.warn("[orderStore] tracking preset auto-apply failed", e); }
    })();
  }

  saveOrders(orders);

  if (status === "Confirmed" && prevStatus !== "Confirmed") {
    const order = orders[idx];
    const key = getPriceKey(order.deviceName, order.variant, order.grade as Grade, order.screenType as ScreenCondition | undefined);
    decrementStock(key);
  }

  // Automatic email + admin notification fanout for any status change.
  // Guarantees that manual, automatic, preset-driven, and background paths
  // all trigger notifications. The customer-side preview path can also call
  // dispatchOrderStatusEmail directly — duplicate idempotencyKey is handled.
  void (async () => {
    try {
      const { dispatchOrderStatusEmail } = await import("@/lib/orderEmailDispatcher");
      await dispatchOrderStatusEmail(orders[idx], status);
    } catch {}
    try {
      await supabase.functions.invoke("notify-admins", {
        body: {
          type: "order",
          title: `Order ${orders[idx].invoiceId} → ${status}`,
          body: `${orders[idx].customerName} • ${orders[idx].deviceName}`,
          targetUsername: orders[idx].staffAssigned || null,
          metadata: {
            orderId: orders[idx].id,
            invoiceId: orders[idx].invoiceId,
            status,
            previousStatus: prevStatus,
            deviceName: orders[idx].deviceName,
            notification_type: "order_status",
          },
        },
      });
    } catch {}
  })();
}

export function updateOrderField(orderId: string, updates: Partial<Order>): void {
  const orders = loadOrders();
  const idx = orders.findIndex((o) => o.id === orderId);
  if (idx >= 0) {
    orders[idx] = { ...orders[idx], ...updates };
    saveOrders(orders);
  }
}

export function updateOrderStaff(orderId: string, staffUsername: string): void {
  const orders = loadOrders();
  const idx = orders.findIndex((o) => o.id === orderId);
  if (idx >= 0) {
    orders[idx].staffAssigned = staffUsername;
    saveOrders(orders);
  }
}

export function addSmsLog(orderId: string, entry: SmsLogEntry): void {
  const orders = loadOrders();
  const idx = orders.findIndex((o) => o.id === orderId);
  if (idx >= 0) {
    if (!orders[idx].smsLog) orders[idx].smsLog = [];
    orders[idx].smsLog!.push(entry);
    saveOrders(orders);
  }
}

const RECYCLED_ORDERS_KEY = "khays_recycled_orders";

export function loadRecycledOrders(): Order[] {
  try {
    const stored = localStorage.getItem(RECYCLED_ORDERS_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return [];
}

function saveRecycledOrders(orders: Order[]): void {
  localStorage.setItem(RECYCLED_ORDERS_KEY, JSON.stringify(orders));
  emitOrdersChanged();
}

/** Soft delete: move to recycling bin */
export function deleteOrder(orderId: string): void {
  const orders = loadOrders();
  const order = orders.find(o => o.id === orderId);
  if (order) {
    const recycled = loadRecycledOrders();
    recycled.unshift({ ...order, status: order.status });
    saveRecycledOrders(recycled);
  }
  saveOrders(orders.filter(o => o.id !== orderId));
}

/** Permanently delete from recycling bin */
export function permanentlyDeleteRecycledOrder(orderId: string): void {
  const recycled = loadRecycledOrders().filter(o => o.id !== orderId);
  saveRecycledOrders(recycled);
}

/** Restore from recycling bin */
export function restoreRecycledOrder(orderId: string): void {
  const recycled = loadRecycledOrders();
  const order = recycled.find(o => o.id === orderId);
  if (order) {
    const orders = loadOrders();
    orders.unshift(order);
    saveOrders(orders);
    saveRecycledOrders(recycled.filter(o => o.id !== orderId));
  }
}

/** Clear all recycled orders */
export function clearRecycledOrders(): void {
  saveRecycledOrders([]);
}

export const ORDER_STATUSES: OrderStatus[] = [
  "Pending", "Confirmed", "Shipped", "At Warehouse", 
  "Outbound", "Inbound", "Arrived", "Delivering", 
  "Delivered", "Completed", "Enquiry", "Refunded"
];

export const FULFILLMENT_STATUSES: OrderStatus[] = [
  "Pending", "Confirmed", "Shipped", "At Warehouse", 
  "Outbound", "Inbound", "Arrived", "Delivering", 
  "Delivered", "Completed", "Refunded"
];

/** Generate auto WhatsApp message for a status change */
export function getStatusMessage(order: Order, newStatus: OrderStatus): string {
  const deviceDetails = `${order.deviceName} ${order.variant}${order.grade ? ` (${order.grade})` : ""}${order.screenType ? ` - ${order.screenType}` : ""}${order.color ? ` - ${order.color}` : ""}`;
  
  switch (newStatus) {
    case "Confirmed":
      return `Hi ${order.customerName}, your order ${order.invoiceId} for ${deviceDetails} has been confirmed! Thank you for choosing Khay's Premium Pre-Order. We'll keep you updated on your delivery status.`;
    case "Shipped":
      return `Hi ${order.customerName}, great news! Your order ${order.invoiceId} (${order.deviceName}) has been shipped! It's on its way to you. We'll notify you at each step.`;
    case "At Warehouse":
      return `Hi ${order.customerName}, your order ${order.invoiceId} (${order.deviceName}) has arrived at our warehouse and is being processed for dispatch.`;
    case "Outbound":
      return `Hi ${order.customerName}, your order ${order.invoiceId} (${order.deviceName}) is now outbound and heading to your region!`;
    case "Inbound":
      return `Hi ${order.customerName}, your order ${order.invoiceId} (${order.deviceName}) is inbound and approaching your delivery area.`;
    case "Arrived":
      return `Hi ${order.customerName}, your order ${order.invoiceId} (${order.deviceName}) has arrived at the delivery hub near you! Delivery will begin shortly.`;
    case "Delivering":
      return `Hi ${order.customerName}, your order ${order.invoiceId} (${order.deviceName}) is out for delivery! Please stay available to receive your package.`;
    case "Delivered":
      return `Hi ${order.customerName}, your order ${order.invoiceId} (${order.deviceName}) has been delivered! Thank you for shopping with Khay's Premium Pre-Order. Enjoy your new device!`;
    case "Completed": {
      const baseUrl = typeof window !== "undefined" ? window.location.origin : "";
      const reviewLink = `${baseUrl}/review?order=${encodeURIComponent(order.id)}`;
      return `Hi ${order.customerName}, your order ${order.invoiceId} is now complete! We hope you're enjoying your ${order.deviceName}. Thank you for choosing Khay's!\n\nWe'd love to hear your feedback! Leave a review here: ${reviewLink}`;
    }
    case "Refunded":
      return `Hi ${order.customerName}, your order ${order.invoiceId} for ${order.deviceName} has been refunded. Please allow a few business days for the refund to reflect. If you have any questions, feel free to reach out.`;
    default:
      return `Hi ${order.customerName}, your order ${order.invoiceId} status has been updated to: ${newStatus}.`;
  }
}
